import { Box, Button, ButtonGroup } from "@chakra-ui/react";
import { LEVELS, REPORT_TYPE_IDS } from "../../../../../store/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  resetForm,
  setAddReport,
  setActiveReportIndex,
  setActiveExportConfig,
  setActiveReportTypeId,
  updateLocalSubscription,
  updateLevel,
  setSelectedBranches,
} from "../../../../../store/actions/subscriptionsActions";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  resetDrawer,
  updateDrawer,
} from "../../../../../store/actions/miscAction";
import {
  ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  TICKET_SUMMARY_COLUMN_DEFINITIONS,
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
} from "../../../../../store/renderConfig";

const AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.monthly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.daily_branch_tickets_per_agent,
]);

const NO_CHART_AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
]);

const TICKETS_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_tickets_per_service,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_service,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_service,
  REPORT_TYPE_IDS.daily_organization_tickets_per_service,
]);

const SERVICE_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_service_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_service_time_per_service,
  REPORT_TYPE_IDS.daily_branch_service_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_service_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_service_time_per_service,
  REPORT_TYPE_IDS.daily_organization_service_time_per_service,
]);

const WAITING_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_organization_waiting_time_per_service,
]);

const ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_service,
]);

const ARRIVAL_RATES_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_hour,
]);

const TICKET_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_tickets,
  REPORT_TYPE_IDS.weekly_organization_tickets,
  REPORT_TYPE_IDS.daily_organization_tickets,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.daily_organization_tickets_per_branch,
]);

const WAITING_TIME_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_waiting_time,
  REPORT_TYPE_IDS.weekly_organization_waiting_time,
  REPORT_TYPE_IDS.daily_organization_waiting_time,
]);

const CUSTOMERS_IN_BRANCH_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.weekly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.daily_branch_customers_in_branch_per_hour,
]);

const Save = ({}) => {
  const dispatch = useDispatch();
  const {
    subscription,
    activeReportTypeId,
    activeReportIndex,
    editReportTypeId,
    isEditing,
    level,
    selectedBranches,
    activeRenderConfig,
    activeExportConfig,
  } = useSelector((state) => state.subscriptionsReducer);

  const subscriptionClone = _.cloneDeep(subscription);

  const getReportRenderConfig = (reportTypeId) => {
    if (AGENT_TICKETS_REPORT_TYPES.has(reportTypeId)) {
      const normalized = normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
      });
      return NO_CHART_AGENT_TICKETS_REPORT_TYPES.has(reportTypeId)
        ? { ...normalized, charts: { enabled: false } }
        : normalized;
    }
    if (TICKETS_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
      });
    }
    if (SERVICE_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
      });
    }
    if (WAITING_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
      });
    }
    if (ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
      });
    }
    if (ARRIVAL_RATES_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
      });
    }
    if (TICKET_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: TICKET_SUMMARY_COLUMN_DEFINITIONS,
      });
    }
    if (WAITING_TIME_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
      });
    }
    if (CUSTOMERS_IN_BRANCH_REPORT_TYPES.has(reportTypeId)) {
      return normalizeRenderConfig(activeRenderConfig, {
        columnDefinitions: CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
      });
    }
    return activeRenderConfig;
  };

  const saveToLocal = () => {
    if (level === LEVELS.BRANCH && selectedBranches.length === 0) {
      toast.error("Please select branches");
      return;
    }
    if (isEditing && activeReportIndex !== null && activeReportIndex >= 0) {
      //update existing report
      const reportIndex = activeReportIndex;

      //update the report
      const updatedReport = {
        ...subscriptionClone.reports[reportIndex],
        reportTypeId: editReportTypeId || activeReportTypeId,
        branches: selectedBranches || null,
        renderConfig: getReportRenderConfig(editReportTypeId || activeReportTypeId),
        exportConfig: { csv: Boolean(activeExportConfig?.csv) },
      };

      subscriptionClone.reports[reportIndex] = updatedReport;
      dispatch(updateLocalSubscription(subscriptionClone));
      discard();
      return;
    }

    //activeReportType id will be empty when the user deletes all reports during editing
    let listOrder = 0;
    if (!_.isEmpty(subscriptionClone?.reports)) {
      listOrder =
        Math.max(...subscriptionClone?.reports?.map((a) => a.listOrder || 0)) ||
        0;
    }
    listOrder += 1;
    const updatedReports = subscriptionClone.reports
      ? [
          ...subscriptionClone.reports,
          level === LEVELS.BRANCH
            ? {
                reportTypeId: activeReportTypeId || editReportTypeId,
                branches: selectedBranches,
                listOrder: listOrder,
                renderConfig: getReportRenderConfig(activeReportTypeId || editReportTypeId),
                exportConfig: { csv: Boolean(activeExportConfig?.csv) },
              }
            : {
                reportTypeId: activeReportTypeId || editReportTypeId,
                listOrder: listOrder,
                renderConfig: getReportRenderConfig(activeReportTypeId || editReportTypeId),
                exportConfig: { csv: Boolean(activeExportConfig?.csv) },
              },
        ]
      : [
          level === LEVELS.BRANCH
            ? {
                reportTypeId: activeReportTypeId || editReportTypeId,
                branches: selectedBranches,
                listOrder,
                renderConfig: getReportRenderConfig(activeReportTypeId || editReportTypeId),
                exportConfig: { csv: Boolean(activeExportConfig?.csv) },
              }
            : {
                reportTypeId: activeReportTypeId || editReportTypeId,
                listOrder,
                renderConfig: getReportRenderConfig(activeReportTypeId || editReportTypeId),
                exportConfig: { csv: Boolean(activeExportConfig?.csv) },
              },
        ];

    dispatch(
      updateLocalSubscription({
        ...subscriptionClone,
        reports: updatedReports,
      })
    );
    dispatch(updateLevel(""));
    dispatch(setSelectedBranches([]));
    dispatch(setAddReport(false));
    dispatch(setActiveReportTypeId(""));
    dispatch(setActiveExportConfig());
    const title = subscriptionClone?.id
      ? "Edit Reports Subscription"
      : "Create a Reports Subscription";
    const drawer = {
      title,
      isOpen: 1,
      content: { component: "SubscriptionForm", id: null },
    };
    dispatch(updateDrawer(drawer));
  };

  const discard = () => {
    if (activeReportTypeId) {
      const title = subscriptionClone.id
        ? "Edit Reports Subscription"
        : "Create a Reports Subscription";
      const drawer = {
        title,
        isOpen: 1,
        content: { component: "SubscriptionForm", id: null },
      };
      dispatch(setActiveReportTypeId(""));
      dispatch(setActiveReportIndex(null));
      dispatch(setActiveExportConfig());
      dispatch(setAddReport(false));
      dispatch(updateDrawer(drawer));
      return;
    } else if (
      //only the subscriptionName and reportRange
      Object.keys(subscription).length === 2 &&
      Object.keys(subscription).includes("reportRange")
    ) {
      const title = "Create a Reports Subscription";
      const drawer = {
        title,
        isOpen: 1,
        content: { component: "SubscriptionForm", id: null },
      };
      dispatch(setAddReport(false));
      dispatch(
        updateLocalSubscription({ ...subscriptionClone, reportRange: "" })
      );

      dispatch(updateDrawer(drawer));
      return;
    }
    dispatch(resetForm());

    dispatch(resetDrawer());
  };

  return (
    <>
      <Box mt={5}>
        <Box>
          <ButtonGroup>
            <Button
              onClick={discard}
              variant="outline"
              colorScheme="black"
              size="sm"
            >
              Cancel
            </Button>
            {(activeReportTypeId || editReportTypeId) &&
              (level === LEVELS.ORGANIZATION ||
                (selectedBranches.length > 0 && level === LEVELS.BRANCH)) && (
                <Button onClick={saveToLocal} size="sm" bg="#000" color="#fff">
                  {!_.isEmpty(
                    subscription?.reports?.find(
                      (a) => a.reportTypeId === activeReportTypeId
                    )
                  ) && isEditing
                    ? "Update" /* if editing */
                    : "Add"}
                </Button>
              )}
          </ButtonGroup>
        </Box>
      </Box>
    </>
  );
};

export default Save;
